import React from "react";
import { motion } from "framer-motion";
import SectionHeading from "./SectionHeading";
import { projects, testimonial } from "../constants/data";

const ProjectCase = ({ project, index }) => {
    const reversed = index % 2 === 1;

    return (
        <motion.article
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true, margin: "-80px" }}
            className="grid items-center gap-10 border-b border-line py-16 first:pt-0 lg:grid-cols-2 lg:gap-16"
        >
            <div className={`group relative ${reversed ? "lg:order-2" : ""}`}>
                <a
                    href={project.live_link}
                    target="_blank"
                    rel="noreferrer"
                    className="relative block overflow-hidden rounded-2xl border border-line shadow-soft"
                >
                    <img
                        src={project.image}
                        alt={project.name}
                        className="aspect-[16/10] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-fg/60 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                    <span className="absolute bottom-4 left-4 font-mono text-xs uppercase tracking-widest text-white opacity-0 transition-all duration-300 group-hover:opacity-100">
                        Open project ↗
                    </span>
                </a>
            </div>

            <div className={reversed ? "lg:order-1" : ""}>
                <div className="mb-4 flex items-baseline gap-4 font-mono text-xs text-faint">
                    <span className="text-accent">/{project.index}</span>
                    <span>{project.year}</span>
                    <span className="uppercase tracking-widest">{project.role}</span>
                </div>

                <h3 className="mb-5 text-3xl font-medium tracking-tight text-fg sm:text-4xl">
                    {project.name}
                </h3>

                <p className="mb-6 leading-relaxed text-muted">
                    {project.description}
                </p>

                {project.highlights && (
                    <ul className="mb-7 space-y-2">
                        {project.highlights.map((h, i) => (
                            <li
                                key={i}
                                className="flex gap-3 text-sm text-muted"
                            >
                                <span className="font-mono text-accent">→</span>
                                {h}
                            </li>
                        ))}
                    </ul>
                )}

                <div className="mb-8 flex flex-wrap gap-2">
                    {project.tags.map((tag) => (
                        <span
                            key={tag}
                            className="rounded-full border border-line bg-elevated px-3 py-1 font-mono text-xs text-muted"
                        >
                            {tag}
                        </span>
                    ))}
                </div>

                <div className="flex gap-6 font-mono text-xs uppercase tracking-widest">
                    {project.live_link && (
                        <a
                            href={project.live_link}
                            target="_blank"
                            rel="noreferrer"
                            className="link-sweep pb-1 text-fg transition-colors hover:text-accent"
                        >
                            Live ↗
                        </a>
                    )}
                    {project.source_code_link && (
                        <a
                            href={project.source_code_link}
                            target="_blank"
                            rel="noreferrer"
                            className="link-sweep pb-1 text-muted transition-colors hover:text-accent"
                        >
                            Source ↗
                        </a>
                    )}
                </div>
            </div>
        </motion.article>
    );
};

const Portfolio = () => {
    return (
        <section id="work" className="py-28">
            <div className="mx-auto max-w-7xl px-5 sm:px-8">
                <SectionHeading
                    index="03"
                    label="Selected Work"
                    title={
                        <>
                            Things I've shipped that{" "}
                            <span className="font-serif italic text-accent">
                                people actually use
                            </span>
                            .
                        </>
                    }
                />

                <div>
                    {projects.map((project, index) => (
                        <ProjectCase
                            key={project.name}
                            project={project}
                            index={index}
                        />
                    ))}
                </div>

                {/* Testimonial */}
                <motion.figure
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true, margin: "-80px" }}
                    className="mx-auto max-w-3xl pt-20 text-center"
                >
                    <span className="font-serif text-6xl leading-none text-accent">
                        “
                    </span>
                    <blockquote className="font-serif text-2xl italic leading-relaxed text-fg sm:text-3xl">
                        {testimonial.quote}
                    </blockquote>
                    <figcaption className="mt-8 font-mono text-xs uppercase tracking-widest text-muted">
                        {testimonial.name} —{" "}
                        <span className="text-faint">{testimonial.designation}</span>
                    </figcaption>
                </motion.figure>
            </div>
        </section>
    );
};

export default Portfolio;